import { Button } from '@cimplico/components';
import { FormProvider, useForm } from 'react-hook-form';
import { toastNotification } from '../../components/toast/Toast';
import { UpdateWorkpaperFields, useUpdateWorkpapers } from '../../queries/use-update-workpapers';
import {
    TrialBalanceAccountingBasisEnum,
    Workpaper,
    WorkpaperAssignee,
    WorkpaperPermissions,
    WorkpaperRoleEnum,
    WorkpaperSourceEnum,
} from '../../types/workpaper';
import { useState } from 'react';
import { unCamelCase } from '../../utilities/formatting';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import EditWorkpaperConfirm from './EditWorkpaperConfirm';
import UploadTrialBalanceModal from './UploadTrialBalanceModal';
import TrialBalanceSection from './edit-workpaper-sections/TrialBalanceSection';
import AssigneesSection from './edit-workpaper-sections/AssigneesSection';
import TemplatesSection from './edit-workpaper-sections/TemplatesSection';
import SecuritySection from './edit-workpaper-sections/SecuritySection';
import WorkpaperDetailsSection from './edit-workpaper-sections/DetailsSection';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type EditWorkpaperFormFields = {
    name: string;
    periodEnd: string;
    accountingBasis: TrialBalanceAccountingBasisEnum;
    materialityLimit: string;
    materialityLimitType: string;
    dueDate: string;
    approvers: WorkpaperAssignee[];
    reviewers: WorkpaperAssignee[];
    preparers: WorkpaperAssignee[];
    guests: WorkpaperAssignee[];
    procedureTemplateId: string;
    checklistId: string;
    private: boolean;
    permissions: WorkpaperPermissions;
    priorWorkpaperId: string;
};

type EditWorkpaperFormProps = {
    onXeroError: (error?: { name: string; message: string }) => void;
};

function EditWorkpaperForm({ onXeroError }: EditWorkpaperFormProps): JSX.Element {
    const { trackEvent } = useIntercom();
    const updateWorkpapersQuery = useUpdateWorkpapers();
    const [guestError, setGuestError] = useState(false);
    const [changedFields, setChangedFields] = useState<UpdateWorkpaperFields>({});
    const [showUploadTBModal, setShowUploadTBModal] = useState(false);
    const [showEditWorkpaperConfirm, setShowEditWorkpaperConfirm] = useState(false);

    const workpaper = useWorkpaperContext();
    const { isExternalUser } = useIsFirmRole();

    const isDisabled = isExternalUser || workpaper.readOnly || updateWorkpapersQuery.isLoading;

    let limit = null;
    if (workpaper.materialityLimit) {
        if (workpaper.materialityLimit.isPercentage) {
            limit = workpaper.materialityLimit.amount;
        } else {
            limit = workpaper.materialityLimit.amount / 100;
        }
    }

    const methods = useForm<EditWorkpaperFormFields>({
        defaultValues: {
            name: workpaper.name,
            periodEnd: workpaper.periodEnd,
            accountingBasis: workpaper.accountingBasis,
            materialityLimit: limit?.toString(),
            materialityLimitType: workpaper.materialityLimit?.isPercentage ? 'percentage' : 'monetary',
            dueDate: workpaper.dueDate,
            approvers: [...workpaper.assigned, ...(workpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.APPROVER
            ),
            reviewers: [...workpaper.assigned, ...(workpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.REVIEWER
            ),
            preparers: [...workpaper.assigned, ...(workpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.PREPARER
            ),
            guests: [...workpaper.assigned, ...(workpaper.pendingExternalUsers || [])].filter((u) => u.role === WorkpaperRoleEnum.GUEST),
            procedureTemplateId: workpaper.procedureTemplateId,
            checklistId: workpaper.checklistId,
            private: workpaper.private,
            permissions: workpaper.permissions,
            priorWorkpaperId: workpaper.priorWorkpaperId,
        },
        reValidateMode: 'onBlur',
    });

    // Format update workpaper results into error messages array to display in toast
    function formatErrorResults(
        errorFields: { [key: string]: string },
        toUpdateFields: UpdateWorkpaperFields
    ): { errorMessages: string[]; successFields: string[] } {
        let successFields = Object.keys(toUpdateFields).filter(
            (field) => toUpdateFields[field as keyof UpdateWorkpaperFields] !== undefined
        );

        const errorMessages: string[] = [];

        // If any field in this array failed to update, nothing will be updated
        const relatedFields = [
            'name',
            'accountingBasis',
            'periodEnd',
            'dueDate',
            'materialityLimit',
            'materialityLimitAsPercentage',
            'procedureTemplateId',
            'checklistId',
            'private',
        ];

        // Handle error for updating workpaper details
        const nameError = errorFields?.name;
        const otherError = errorFields?.errorMessage;

        if (nameError || otherError) {
            if (nameError) {
                methods.setError('name', { message: nameError });
                errorMessages.push(`Workpaper name - ${nameError}`);
            }

            if (otherError) {
                // Handle error for updating workpaper assignees
                if (otherError.includes('assigned')) {
                    errorMessages.push(`Workpaper assignees - ${otherError}`);
                } else {
                    const otherErrorFields = Object.keys(toUpdateFields)
                        .filter((f) => relatedFields.includes(f) && toUpdateFields[f as keyof UpdateWorkpaperFields] !== undefined)
                        .map((fieldName) => unCamelCase(fieldName));
                    errorMessages.push(`${otherErrorFields.length > 0 ? `${otherErrorFields.join(', ')} -` : ''}${otherError}`);
                }
            }

            return { successFields: [], errorMessages };
        }

        // Handle error for updating workpaper guests
        const guestsError = errorFields?.guests;
        if (guestsError) {
            methods.setError('guests', { message: guestsError });
            errorMessages.push(`Workpaper guests - ${guestsError}`);

            successFields = successFields.filter((f) => f !== 'guests');
        }

        // Handle error for updating Xero workpaper periodEnd and accountingBasis
        const xeroErrorName = errorFields?.xeroErrorName;
        const refreshError = errorFields?.refresh;
        if (refreshError) {
            if (xeroErrorName && refreshError) {
                onXeroError({ message: refreshError, name: xeroErrorName });
            }

            if (toUpdateFields.accountingBasis) {
                methods.setError('accountingBasis', { message: refreshError });
                errorMessages.push(`Trial balance accounting basis - ${refreshError}`);
            }
            if (toUpdateFields.periodEnd) {
                methods.setError('periodEnd', { message: refreshError });
                errorMessages.push(`Workpaper period end - ${refreshError}`);
            }

            successFields = successFields.filter((f) => f !== 'accountingBasis' && f !== 'periodEnd');
        }

        return { successFields, errorMessages };
    }

    // Compare previous workpaper details with updated form values
    function findToUpdateFields(): UpdateWorkpaperFields {
        const {
            name,
            periodEnd,
            accountingBasis,
            dueDate,
            approvers,
            reviewers,
            preparers,
            guests,
            materialityLimit,
            materialityLimitType,
            procedureTemplateId,
            checklistId,
            permissions,
            private: _private,
            priorWorkpaperId,
        } = methods.getValues();

        // Get only users that already exist in the firm (not users who haven't accepted invitation)
        const assigned: { userId: string; role: WorkpaperRoleEnum }[] = [...approvers, ...reviewers, ...preparers, ...guests]
            .filter((a) => a.id)
            .map((u) => ({ userId: u.id as string, role: u.role }));

        let assignedDiff = false;

        // Compare difference in workpaper assignees
        if (assigned.length !== (workpaper.assigned || []).length) {
            assignedDiff = true;
        } else if (!assigned.every((a) => (workpaper.assigned || []).find((wa) => wa.id === a.userId && wa.role === a.role))) {
            assignedDiff = true;
        }

        const pendingExternalUsers = [...approvers, ...reviewers, ...preparers, ...guests].filter((user) => !user.id && user.email);

        let pendingExternalUsersDiff = false;

        // Compare difference in pending external users
        if (pendingExternalUsers.length !== (workpaper.pendingExternalUsers || []).length) {
            pendingExternalUsersDiff = true;
        } else if (
            !pendingExternalUsers.every((u) =>
                (workpaper.pendingExternalUsers || []).find((user) => user.role === u.role && user.email === u.email)
            )
        ) {
            pendingExternalUsersDiff = true;
        }

        const isPercentage = materialityLimitType === 'percentage';
        const newMaterialityLimit = materialityLimit ? (isPercentage ? +materialityLimit : +materialityLimit * 100) : '';
        const prevMaterialityLimit = workpaper.materialityLimit ? workpaper.materialityLimit.amount : '';
        const materialityChanged =
            prevMaterialityLimit.toString() !== newMaterialityLimit.toString() ||
            isPercentage !== (workpaper.materialityLimit?.isPercentage || false);

        const changedFields = {
            name: name !== workpaper.name ? name : undefined,
            dueDate: dueDate !== workpaper.dueDate ? dueDate : undefined,
            periodEnd: periodEnd !== workpaper.periodEnd ? periodEnd : undefined,
            accountingBasis: accountingBasis !== workpaper.accountingBasis ? accountingBasis : undefined,
            assigned: assignedDiff ? assigned : undefined,
            materialityLimit: materialityChanged
                ? {
                      amount: materialityLimit ? +materialityLimit : null,
                      isPercentage: materialityLimit ? materialityLimitType === 'percentage' : null,
                  }
                : undefined,
            pendingExternalUsers: pendingExternalUsersDiff ? pendingExternalUsers : undefined,
            procedureTemplateId: procedureTemplateId !== workpaper.procedureTemplateId ? procedureTemplateId : undefined,
            checklistId: checklistId !== workpaper.checklistId ? checklistId : undefined,
            private: _private !== workpaper.private ? _private : undefined,
            permissions: JSON.stringify(permissions) !== JSON.stringify(workpaper.permissions) ? permissions : undefined,
            priorWorkpaperId: priorWorkpaperId !== workpaper.priorWorkpaperId ? priorWorkpaperId : undefined,
        };

        return changedFields;
    }

    // On update workpaper, update form default values
    function updateDefaultValues(newWorkpaper: Workpaper): void {
        let newLimit = null;
        if (newWorkpaper.materialityLimit) {
            if (newWorkpaper.materialityLimit.isPercentage) {
                newLimit = newWorkpaper.materialityLimit.amount;
            } else {
                newLimit = newWorkpaper.materialityLimit.amount / 100;
            }
        }

        methods.reset({
            name: newWorkpaper.name,
            periodEnd: newWorkpaper.periodEnd,
            accountingBasis: newWorkpaper.accountingBasis,
            materialityLimit: newLimit?.toString(),
            materialityLimitType: newWorkpaper.materialityLimit?.isPercentage ? 'percentage' : 'monetary',
            dueDate: newWorkpaper.dueDate,
            approvers: [...newWorkpaper.assigned, ...(newWorkpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.APPROVER
            ),
            reviewers: [...newWorkpaper.assigned, ...(newWorkpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.REVIEWER
            ),
            preparers: [...newWorkpaper.assigned, ...(newWorkpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.PREPARER
            ),
            guests: [...newWorkpaper.assigned, ...(newWorkpaper.pendingExternalUsers || [])].filter(
                (u) => u.role === WorkpaperRoleEnum.GUEST
            ),
            procedureTemplateId: newWorkpaper.procedureTemplateId,
            checklistId: newWorkpaper.checklistId,
            private: newWorkpaper.private,
            permissions: newWorkpaper.permissions,
            priorWorkpaperId: newWorkpaper.priorWorkpaperId,
        });
    }

    // Submit update workpaper settings form
    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (guestError || isExternalUser) {
            return;
        }

        const { periodEnd, dueDate, approvers, procedureTemplateId, checklistId } = methods.getValues();

        // Check if due date is before periodEnd date and currentDate
        if (dueDate && periodEnd && new Date(dueDate) < new Date(periodEnd)) {
            methods.setError('dueDate', { message: 'Due date must be later than the period end date.' });
            return;
        }

        if (workpaper.procedureTemplateId && procedureTemplateId === undefined) {
            methods.setError('procedureTemplateId', { message: 'Procedure template must not be removed for an open workpaper.' });
            return;
        }

        if (workpaper.checklistId && checklistId === undefined) {
            methods.setError('checklistId', { message: 'Checklist template must not be removed for an open workpaper.' });
            return;
        }

        if (approvers.length === 0) {
            methods.setError('approvers', { message: 'An approver is required.' });
            return;
        }

        const toUpdateFields = findToUpdateFields();
        setChangedFields(toUpdateFields);

        // if template changes, show edit confirmation dialog
        if (toUpdateFields.procedureTemplateId || toUpdateFields.checklistId) {
            setShowEditWorkpaperConfirm(true);
            return;
        }

        // if no values have changed (all fields are UNDEFINED), return
        if (Object.values(toUpdateFields).every((val) => val === undefined)) {
            toastNotification('success', 'No changes have been made!');
            return;
        }

        updateWorkpaper(toUpdateFields);
    }

    // Send a request to update workpaper details
    function updateWorkpaper(fields: UpdateWorkpaperFields): void {
        setShowEditWorkpaperConfirm(false);

        updateWorkpapersQuery.mutate([{ id: workpaper.id, fields }], {
            onSuccess: (e) => {
                toastNotification('success', 'Successfully updated settings!');

                if (workpaper.provider === WorkpaperSourceEnum.XERO && e.results[0].data?.refreshXeroTrialBalanceResult) {
                    const { createdCount, updatedCount, excludedCount } = e.results[0].data.refreshXeroTrialBalanceResult;
                    toastNotification(
                        'success',
                        'Trial balance updated!',
                        `${createdCount} created. ${updatedCount} updated. ${excludedCount} excluded.`
                    );
                }

                if (e.results[0].data?.workpaper) {
                    updateDefaultValues(e.results[0].data?.workpaper);
                }

                // if accounting basis has changed, show upload tb modal to prompt user to upload new trial balance for the new accounting basis
                if (fields.accountingBasis && !workpaper.provider) {
                    setShowUploadTBModal(true);
                }

                if (fields.priorWorkpaperId && e.results[0].data?.workpaper.priorWorkpaperId) {
                    trackEvent(
                        intercomEvents.carryForward.linkPriorWorkpaper.event,
                        intercomEvents.carryForward.linkPriorWorkpaper.metaData(workpaper.id, e.results[0].data?.workpaper.priorWorkpaperId)
                    );
                }
            },
            onError: (e) => {
                const results = e.response?.data.results || [];
                const errorFields = results[0]?.error?.fields;

                const { successFields, errorMessages } = formatErrorResults(errorFields, fields);

                if (successFields.length > 0) {
                    toastNotification('success', 'Successfully updated some workpaper details!');
                }

                toastNotification('error', 'Unable to update the following workpaper details:', errorMessages);
            },
        });
    }

    return (
        <FormProvider {...methods}>
            <div className="w-[380px]">
                <fieldset disabled={updateWorkpapersQuery.isLoading || isExternalUser}>
                    {/* Dialog to prompt user to confirm that they are trying to update procedure/checklist template */}
                    {showEditWorkpaperConfirm && (
                        <EditWorkpaperConfirm
                            procedureTemplateChange={changedFields.procedureTemplateId !== undefined}
                            checklistTemplateChange={changedFields.checklistId !== undefined}
                            onClose={(): void => setShowEditWorkpaperConfirm(false)}
                            onEdit={(): void => updateWorkpaper(changedFields)}
                        />
                    )}

                    {/* Dialog to prompt user to upload new trial balance for MANUAL workpaper */}
                    {showUploadTBModal && (
                        <UploadTrialBalanceModal
                            onClose={(): void => setShowUploadTBModal(false)}
                            onSave={(): void => setShowUploadTBModal(false)}
                        />
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-8 max-w-[375px]">
                            <WorkpaperDetailsSection isDisabled={isDisabled} />
                            <TrialBalanceSection isDisabled={isDisabled} />
                            <AssigneesSection isLoading={updateWorkpapersQuery.isLoading} onExternalUserError={setGuestError} />
                            <TemplatesSection isDisabled={isDisabled} />
                            <SecuritySection isDisabled={isDisabled} />
                            <div className="mr-0 ml-auto w-fit pt-8">
                                <Button
                                    type="primary"
                                    label={updateWorkpapersQuery.isLoading ? 'Saving...' : 'Save'}
                                    isSubmit
                                    isLoading={updateWorkpapersQuery.isLoading}
                                    disabled={updateWorkpapersQuery.isLoading}
                                />
                            </div>
                        </div>
                    </form>
                </fieldset>
            </div>
        </FormProvider>
    );
}
export default EditWorkpaperForm;
